import { Upload } from 'lucide-react';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { StoneImportResult, Supplier } from '@nucleus-commerce/api-client/platform';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { MAX_CSV_LENGTH } from '../api/mappers';
import { useImportStones } from '../hooks/use-import-stones';
import { importSchema, type ImportValues } from '../schemas/supplier-schema';

/** Bad rows listed in the dialog; the rest are summarised by count. */
const SHOWN_ERRORS = 50;

export interface ImportStonesDialogProps {
  suppliers: readonly Supplier[];
}

/**
 * Loads a supplier's CSV into the shared pool. Rows upsert by supplier and stone number, so a
 * re-upload of the same feed updates prices instead of duplicating stones.
 */
export function ImportStonesDialog({ suppliers }: ImportStonesDialogProps) {
  const { t } = useTranslation('diamonds');
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Upload aria-hidden />
          {t('import.open')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription>{t('import.description')}</DialogDescription>
        </DialogHeader>
        {open ? <ImportForm suppliers={suppliers} onDone={() => setOpen(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ImportForm({ suppliers, onDone }: ImportStonesDialogProps & { onDone: () => void }) {
  const { t } = useTranslation(['diamonds', 'common']);
  const fileId = useId();
  const upload = useImportStones();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<StoneImportResult | null>(null);
  const form = useForm<ImportValues>({
    resolver: useI18nResolver(importSchema),
    defaultValues: { supplierId: suppliers.length === 1 ? (suppliers[0]?.id ?? '') : '' },
  });

  const onSubmit = form.handleSubmit(async ({ supplierId }) => {
    if (!file) {
      setFileError(t('diamonds:import.fileRequired'));
      return;
    }
    const csv = await file.text();
    if (csv.length > MAX_CSV_LENGTH) {
      setFileError(t('diamonds:import.fileTooLarge'));
      return;
    }
    setFileError(null);
    upload.mutate({ supplierId, csv }, { onSuccess: setResult });
  });

  if (result) {
    return (
      <div className="flex flex-col gap-4">
        <Alert role="status" variant={result.errors.length > 0 ? 'destructive' : 'default'}>
          <AlertTitle>
            {t('diamonds:import.done', {
              created: result.created,
              updated: result.updated,
              skipped: result.skipped,
            })}
          </AlertTitle>
          {result.errors.length > 0 ? (
            <AlertDescription>{t('diamonds:import.rowsSkipped')}</AlertDescription>
          ) : null}
        </Alert>
        {result.errors.length > 0 ? (
          <ul
            className="max-h-60 overflow-y-auto rounded-md border text-sm"
            aria-label={t('diamonds:import.errorsLabel')}
          >
            {result.errors.slice(0, SHOWN_ERRORS).map((error) => (
              <li
                key={`${error.row}-${error.message}`}
                className="border-b px-3 py-1.5 last:border-0"
              >
                {t('diamonds:import.rowError', { row: error.row, message: error.message })}
              </li>
            ))}
            {result.errors.length > SHOWN_ERRORS ? (
              <li className="text-muted-foreground px-3 py-1.5">
                {t('diamonds:import.moreErrors', { count: result.errors.length - SHOWN_ERRORS })}
              </li>
            ) : null}
          </ul>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setResult(null)}>
            {t('diamonds:import.another')}
          </Button>
          <Button type="button" onClick={onDone}>
            {t('common:actions.close')}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {upload.isError ? (
          <ServerError
            error={upload.error}
            message={
              upload.error.code === 'LIMIT_EXCEEDED'
                ? t('diamonds:import.tooManyRows', { limit: upload.error.meta.limit })
                : upload.error.fieldErrors['body.csv']
                  ? t('diamonds:import.noRows')
                  : null
            }
          />
        ) : null}
        <FormField
          control={form.control}
          name="supplierId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('diamonds:import.supplier')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('diamonds:import.supplierPlaceholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} ({supplier.currency})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-2">
          <Label htmlFor={fileId}>{t('diamonds:import.file')}</Label>
          <Input
            id={fileId}
            type="file"
            accept=".csv,text/csv"
            aria-invalid={fileError !== null}
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setFileError(null);
            }}
          />
          <p className="text-muted-foreground text-xs">{t('diamonds:import.fileHint')}</p>
          {fileError ? (
            <p className="text-destructive text-sm" role="alert">
              {fileError}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={upload.isPending}>
            {upload.isPending ? t('diamonds:import.uploading') : t('diamonds:import.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
